import { useMemo, useState } from "react";
import { ModalShell } from "../../components/ModalShell";
import { Skeleton } from "../../components/dashboard/Skeleton";
import { Toast, useToast } from "../../components/Toast";
import { SaveSiteLoginModal } from "../../components/dashboard/site-logins/SaveSiteLoginModal";
import { SITE_LOGIN_MAX_PER_TENANT, type SiteLogin, useSiteLogins } from "../../hooks/useSiteLogins";
import { formatDateTime } from "../../utils/formatDateTime";
import { useDashboardData } from "./useDashboardData";
import dialogStyles from "../admin/AdminFeatureFlags.module.css";
import modalStyles from "../../components/dashboard/site-logins/SiteLoginModals.module.css";
import styles from "./SiteLogins.module.css";

// Website sign-ins for one instance. The customer saves a username and
// password for a site their work orders live behind; the instance can then
// open a page there without ever holding the credential itself.
export const SiteLogins = (): JSX.Element => {
  const { status: dashStatus, instances } = useDashboardData();
  const { toast, showToast, dismissToast } = useToast();
  const [selectedId, setSelectedId] = useState<string>("");
  const [editing, setEditing] = useState<SiteLogin | null>(null);
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<SiteLogin | null>(null);
  const [deleteBusy, setDeleteBusy] = useState<boolean>(false);

  // No shared "current instance" state exists in the dashboard - each page
  // picks its own - so default to the first and only show a picker when
  // there is actually a choice to make.
  const tenantId = useMemo(() => {
    const match = instances.find((i) => i.id === selectedId);
    return match?.id ?? instances[0]?.id ?? null;
  }, [instances, selectedId]);

  const { status, logins, errorMessage, saveLogin, deleteLogin } = useSiteLogins(tenantId);
  const noInstances = dashStatus === "ready" && instances.length === 0;
  const atLimit = logins.length >= SITE_LOGIN_MAX_PER_TENANT;

  const handleDelete = async (login: SiteLogin): Promise<void> => {
    setDeleteBusy(true);
    const result = await deleteLogin(login.name);
    setDeleteBusy(false);
    setDeleting(null);
    showToast(
      result.ok ? `Removed "${login.name}".` : `Remove failed: ${result.message}`,
      result.ok ? "success" : "error",
    );
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>Website sign-ins</h1>
          <p className={styles.subtitle}>
            Save logins for supplier or client portals so your assistant can open work orders there.
          </p>
        </div>
        {/* The server refuses the eleventh with a 409. Said here instead, so the
            limit is something the customer can see rather than something they
            discover by filling a form in and losing it. */}
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={() => setAddOpen(true)}
          disabled={tenantId === null || atLimit}
          title={atLimit ? `You can save at most ${SITE_LOGIN_MAX_PER_TENANT} sign-ins.` : undefined}
          data-testid="new-site-login"
        >
          Add a sign-in
        </button>
      </header>

      <div className={styles.assurance}>
        <span>
          Stored encrypted on our servers, never on your instance. We sign in for your assistant and
          pass back only the page, so it never holds the password.
        </span>
      </div>

      {instances.length > 1 && (
        <div className={styles.toolbar}>
          <label className={styles.instancePicker}>
            <span>Instance</span>
            <select
              className={styles.select}
              value={tenantId ?? ""}
              onChange={(e) => setSelectedId(e.target.value)}
              data-testid="site-logins-instance"
            >
              {instances.map((instance) => (
                <option key={instance.id} value={instance.id}>
                  {instance.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {noInstances && (
        <div className={styles.emptyCard} data-testid="site-logins-no-instance">
          <span>You&apos;ll be able to save website sign-ins once your instance is set up.</span>
        </div>
      )}

      {!noInstances && status === "loading" && (
        <ul className={styles.list} aria-hidden>
          {[0, 1].map((i) => (
            <li key={i} className={styles.skeletonRow}>
              <Skeleton width="40%" height="1rem" />
              <Skeleton width="15%" height="1rem" />
            </li>
          ))}
        </ul>
      )}

      {status === "error" && (
        <div className={styles.errorCard}>{errorMessage ?? "Failed to load website sign-ins."}</div>
      )}

      {!noInstances && status === "ready" && logins.length === 0 && (
        <div className={styles.emptyCard} data-testid="site-logins-empty">
          <span>No sign-ins yet. Add one for any portal you&apos;d otherwise log into by hand.</span>
        </div>
      )}

      {status === "ready" && logins.length > 0 && (
        <ul className={styles.list} data-testid="site-logins-list">
          {logins.map((login) => (
            <li key={login.name} className={styles.row} data-testid="site-login-row">
              <span className={styles.loginMain}>
                <span className={styles.loginName}>{login.name}</span>
                {/* The site is shown because it is the rule, not a detail: this
                    sign-in is used there and nowhere else, so a customer
                    wondering why a page did not open should be able to see
                    what it is pinned to without opening the editor. */}
                <span className={styles.loginMeta} title={formatDateTime(login.updatedAt)}>
                  {/* A sign-in saved before the website became a field has none.
                      Rendering it anyway leaves a dangling separator, and the
                      row is exactly where someone should notice the gap. */}
                  {login.site.length > 0 ? `${login.site} \u00b7 ` : ""}
                  {login.username} &middot; updated {formatDateTime(login.updatedAt)}
                </span>
              </span>
              <span className={styles.rowRight}>
                <button
                  type="button"
                  className={styles.rowBtn}
                  onClick={() => setEditing(login)}
                >
                  Update
                </button>
                <button
                  type="button"
                  className={`${styles.rowBtn} ${styles.deleteBtn}`}
                  onClick={() => setDeleting(login)}
                >
                  Remove
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {(addOpen || editing) && (
        <SaveSiteLoginModal
          existing={editing}
          onClose={() => {
            setAddOpen(false);
            setEditing(null);
          }}
          onSave={async (input) => {
            const result = await saveLogin(input);
            if (result.ok) {
              const wasUpdate = editing !== null;
              setAddOpen(false);
              setEditing(null);
              showToast(
                wasUpdate
                  ? `Updated "${result.value.name}".`
                  : `Saved "${result.value.name}".`,
              );
              return { ok: true, message: "" };
            }
            return { ok: false, message: result.message };
          }}
        />
      )}

      {deleting && (
        <ModalShell density="compact" onClose={() => setDeleting(null)} titleId="delete-site-login-title" size="sm">
          <header className={dialogStyles.dialogHeader}>
            <h3 className={dialogStyles.dialogTitle} id="delete-site-login-title">
              Remove website sign-in
            </h3>
            <button
              type="button"
              className={dialogStyles.closeBtn}
              onClick={() => setDeleting(null)}
              aria-label="Close"
            >
              ×
            </button>
          </header>
          <p className={modalStyles.hint}>
            &quot;{deleting.name}&quot; will be deleted. Anything set up to open pages on that
            site will stop being able to sign in.
          </p>
          <footer className={modalStyles.footer}>
            <button
              type="button"
              className={modalStyles.secondaryBtn}
              onClick={() => setDeleting(null)}
              disabled={deleteBusy}
            >
              Cancel
            </button>
            <button
              type="button"
              className={modalStyles.dangerBtn}
              onClick={() => void handleDelete(deleting)}
              disabled={deleteBusy}
            >
              {deleteBusy ? "Removing…" : "Remove sign-in"}
            </button>
          </footer>
        </ModalShell>
      )}

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};
